<template>
  <div class="vx-col w-full mb-base" id="account-info-col-1">
    <vx-card title="Generating Reports">
      <div class="vx-col flex-1">
        <div class="vx-row mb-5">
          <div class="vx-col md:w-1/3 w-full mt-5">
            <span class="text-sm">Employee Status</span>
            <v-select
              class="w-full"
              v-model="employeeStatusLookup"
              label="status"
              :options="getEmployeeStatusLookup"
              placeholder="Please Select"
            />
          </div>
          <div class="vx-col md:w-1/3 w-full mt-5">
            <span class="text-sm">Employment Status</span>
            <v-select
              class="w-full"
              v-model="employmentStatusLookup"
              label="employStatus"
              :options="getEmploymentStatusLookup"
              placeholder="Please Select"
            />
          </div>
          <div class="vx-col md:w-1/3 w-full mt-5">
            <span class="text-sm">Department</span>
            <v-select
              class="w-full"
              v-model="department"
              label="departmentName"
              :options="getDepartmentLookup"
              placeholder="Please Select"
            />
          </div>
        </div>

        <div class="report-tabs mb-6" role="tablist" aria-label="Report type">
          <button
            type="button"
            class="report-tab"
            :class="{ 'report-tab--active': activeReportTab === 'standard' }"
            role="tab"
            :aria-selected="activeReportTab === 'standard'"
            @click="activeReportTab = 'standard'"
          >
            Standard Reports
            <span v-if="selectedStandardReportCount" class="report-tab__count">
              {{ selectedStandardReportCount }}
            </span>
          </button>
          <button
            type="button"
            class="report-tab"
            :class="{ 'report-tab--active': activeReportTab === 'individual' }"
            role="tab"
            :aria-selected="activeReportTab === 'individual'"
            @click="activeReportTab = 'individual'"
          >
            Individual Payslips
            <span v-if="selectedUserIds.length" class="report-tab__count">
              {{ selectedUserIds.length }}
            </span>
          </button>
        </div>

        <div v-show="activeReportTab === 'standard'" role="tabpanel">
          <div class="report-section mb-6">
          <div class="report-section__header">
            <div>
              <h4 class="font-semibold">Crystal Reports</h4>
              <p class="text-sm text-grey mt-1">
                Choose one or more output formats for each report.
              </p>
            </div>
            <div class="report-section__controls">
              <span class="report-selection-count">
                {{ selectedStandardReportCount }} report{{
                  selectedStandardReportCount === 1 ? "" : "s"
                }} selected
              </span>
              <span class="report-source-badge report-source-badge--crystal">
                Crystal Report
              </span>
              <button
                type="button"
                class="report-select-button"
                :class="{ 'report-select-button--active': areAllCrystalReportsSelected() }"
                @click="toggleAllCrystalReports"
              >
                {{
                  areAllCrystalReportsSelected()
                    ? "Clear all reports"
                    : "Select all reports"
                }}
              </button>
            </div>
          </div>

          <div class="report-table-wrapper">
            <table class="report-table">
              <thead>
                <tr>
                  <th>Report</th>
                  <th v-for="format in reportFormats" :key="format.value">
                    <button
                      type="button"
                      class="report-format-toggle"
                      :class="{
                        [`report-format-toggle--${format.value.toLowerCase()}`]: true,
                        'report-format-toggle--active': isCrystalFormatSelected(
                          format.value
                        ),
                      }"
                      role="checkbox"
                      :aria-checked="isCrystalFormatSelected(format.value)"
                      @click="toggleCrystalFormat(format.value)"
                    >
                      <span class="report-format-toggle__label">
                        {{ format.label }}
                      </span>
                      <span class="report-format-toggle__selector">
                        <span class="report-format-toggle__indicator">
                          {{ isCrystalFormatSelected(format.value) ? "✓" : "" }}
                        </span>
                        {{
                          isCrystalFormatSelected(format.value)
                            ? "All selected"
                            : "Select all"
                        }}
                      </span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="report in crystalReports" :key="report.value">
                  <td
                    class="report-name-cell"
                    :class="{
                      'report-name-cell--active': isAnyReportFormatSelected(report),
                    }"
                    role="checkbox"
                    tabindex="0"
                    :aria-checked="isAnyReportFormatSelected(report)"
                    :aria-label="`Select all available formats for ${report.reportName}`"
                    @click="toggleCrystalReport(report)"
                    @keydown.enter.prevent="toggleCrystalReport(report)"
                    @keydown.space.prevent="toggleCrystalReport(report)"
                  >
                    <div class="font-medium">{{ report.reportName }}</div>
                    <span class="report-name-cell__hint">
                      {{
                        areAllReportFormatsSelected(report)
                          ? "Clear formats"
                          : "Select available formats"
                      }}
                    </span>
                  </td>
                  <td
                    v-for="format in reportFormats"
                    :key="`${report.value}-${format.value}`"
                  >
                    <div class="report-checkbox-cell">
                      <vs-checkbox
                        v-if="report.exportTypes.includes(format.value)"
                        v-model="selected"
                        :vs-value="getReportSelectionValue(report, format.value)"
                        :aria-label="`${report.reportName} ${format.label}`"
                      />
                      <span v-else class="report-format-unavailable">—</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>

          <div class="report-section report-section--backend">
          <div class="report-section__header">
            <div>
              <h4 class="font-semibold">New Excel Reports</h4>
              <p class="text-sm text-grey mt-1">
                These reports are generated directly as Excel files.
              </p>
            </div>
            <div class="report-section__controls">
              <span class="report-source-badge report-source-badge--backend">
                Excel only
              </span>
              <button
                type="button"
                class="report-select-button report-select-button--backend"
                :class="{ 'report-select-button--active': areAllBackendReportsSelected() }"
                @click="toggleAllBackendReports"
              >
                {{
                  areAllBackendReportsSelected()
                    ? "Clear all reports"
                    : "Select all reports"
                }}
              </button>
            </div>
          </div>

          <div class="backend-report-grid">
            <div
              v-for="report in backendReports"
              :key="report.value"
              class="backend-report-option"
              :class="{
                'backend-report-option--active': isReportSelected(
                  report,
                  'EXCEL'
                ),
              }"
              role="checkbox"
              tabindex="0"
              :aria-checked="isReportSelected(report, 'EXCEL')"
              @click="toggleBackendReport(report)"
              @keydown.enter.prevent="toggleBackendReport(report)"
              @keydown.space.prevent="toggleBackendReport(report)"
            >
              <vs-checkbox
                v-model="selected"
                :vs-value="getReportSelectionValue(report, 'EXCEL')"
                @click.native.stop
              >
                <span class="font-medium">{{ report.reportName }}</span>
              </vs-checkbox>
            </div>
          </div>
          </div>
        </div>

        <div v-show="activeReportTab === 'individual'" role="tabpanel">
          <div class="report-section mb-6">
            <div class="report-section__header">
              <div>
                <h4 class="font-semibold">Individual Payslip Templates</h4>
                <p class="text-sm text-grey mt-1">
                  Select the payslip layout and output formats to generate.
                </p>
              </div>
              <div class="report-section__controls">
                <span class="report-source-badge report-source-badge--crystal">
                  Crystal Report
                </span>
                <button
                  type="button"
                  class="report-select-button"
                  :class="{
                    'report-select-button--active': areAllIndividualReportsSelected(),
                  }"
                  @click="toggleAllIndividualReports"
                >
                  {{
                    areAllIndividualReportsSelected()
                      ? "Clear all reports"
                      : "Select all reports"
                  }}
                </button>
              </div>
            </div>

            <div class="report-table-wrapper">
              <table class="report-table">
                <thead>
                  <tr>
                    <th>Report</th>
                    <th v-for="format in reportFormats" :key="format.value">
                      <button
                        type="button"
                        class="report-format-toggle"
                        :class="{
                          [`report-format-toggle--${format.value.toLowerCase()}`]: true,
                          'report-format-toggle--active': isIndividualFormatSelected(
                            format.value
                          ),
                        }"
                        role="checkbox"
                        :aria-checked="isIndividualFormatSelected(format.value)"
                        @click="toggleIndividualFormat(format.value)"
                      >
                        <span class="report-format-toggle__label">
                          {{ format.label }}
                        </span>
                        <span class="report-format-toggle__selector">
                          <span class="report-format-toggle__indicator">
                            {{
                              isIndividualFormatSelected(format.value) ? "✓" : ""
                            }}
                          </span>
                          {{
                            isIndividualFormatSelected(format.value)
                              ? "All selected"
                              : "Select all"
                          }}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="report in individualPayslipReports"
                    :key="report.value"
                  >
                    <td>
                      <div class="font-medium">{{ report.reportName }}</div>
                    </td>
                    <td
                      v-for="format in reportFormats"
                      :key="`${report.value}-${format.value}`"
                    >
                      <div class="report-checkbox-cell">
                        <vs-checkbox
                          v-if="report.exportTypes.includes(format.value)"
                          v-model="selected"
                          :vs-value="getReportSelectionValue(report, format.value)"
                          :aria-label="`${report.reportName} ${format.label}`"
                        />
                        <span v-else class="report-format-unavailable">—</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="report-section employee-selection-section">
            <div class="report-section__header employee-selection-header">
              <div>
                <h4 class="font-semibold">Payslip Recipients</h4>
                <p class="text-sm text-grey mt-1">
                  Choose employees included in this payroll.
                </p>
              </div>
              <button
                type="button"
                class="report-select-button"
                :class="{
                  'report-select-button--active': areAllEmployeesSelected,
                }"
                @click="toggleAllEmployees"
              >
                {{ areAllEmployeesSelected ? "Clear all employees" : "Select all employees" }}
              </button>
            </div>

            <div class="employee-selection-toolbar">
              <vs-input
                v-model="employeeSearch"
                class="employee-search"
                icon-pack="feather"
                icon="icon-search"
                placeholder="Search by employee code or name"
              />
              <span class="text-sm text-grey">
                {{ selectedUserIds.length }} of {{ payrollEmployees.length }} selected
              </span>
            </div>

            <div v-if="filteredPayrollEmployees.length" class="employee-list">
              <div
                v-for="employee in filteredPayrollEmployees"
                :key="employee.userId"
                class="employee-option"
                :class="{
                  'employee-option--active': selectedUserIds.includes(
                    employee.userId
                  ),
                }"
                role="checkbox"
                tabindex="0"
                :aria-checked="selectedUserIds.includes(employee.userId)"
                @click="toggleEmployee(employee.userId)"
                @keydown.enter.prevent="toggleEmployee(employee.userId)"
                @keydown.space.prevent="toggleEmployee(employee.userId)"
              >
                <vs-checkbox
                  v-model="selectedUserIds"
                  :vs-value="employee.userId"
                  @click.native.stop
                />
                <div class="employee-option__details">
                  <span class="font-medium">{{ employee.fullName }}</span>
                  <span class="text-xs text-grey">{{ employee.employeeCode || "No employee code" }}</span>
                </div>
              </div>
            </div>
            <div v-else class="employee-empty-state">
              No employees match your search.
            </div>
          </div>
        </div>
      </div>

      <div class="report-actions mt-5">
        <span class="text-sm text-grey">
          {{ selectedReportCount }} report{{
            selectedReportCount === 1 ? "" : "s"
          }} selected
        </span>
        <vs-button
          @click="onSetPasswordToOpen()"
          :disabled="selected.length === 0"
          color="success"
          type="filled"
          icon-pack="feather"
          icon="icon-download"
          >Generate Reports</vs-button
        >
      </div>
    </vx-card>
  </div>
</template>

<script>
import moment from "moment";
import vSelect from "vue-select";
import JSZip from "xlsx/jszip.js";

import analyticsData from "../../ui-elements/card/analyticsData.js";

import axios from "@/axios.js";
import Swal from "sweetalert2";

export default {
  props: {
    createPayroll: {
      type: Object,
      required: false,
    },
    isValidate6: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      counter: 0,
      percentage: [0],
      analyticsData,

      reportFormats: [
        { value: "PDF", label: "PDF" },
        { value: "RTF", label: "Rich Text" },
        { value: "EXCEL", label: "Excel" },
      ],
      crystalReports: [
        {
          value: "Deductions-Revised",
          reportName: "Deductions - Revised",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "Earnings-Revised",
          reportName: "Earnings - Revised",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "Loans-Revised - Department",
          reportName: "Loans - Revised by Department",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "OvertimeHours",
          reportName: "Overtime Hours",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "ProoflistAmount-Revised",
          reportName: "Prooflist Amount - Revised",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "ProoflistHours-Revised",
          reportName: "Prooflist Hours - Revised",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "PayrollRegister",
          reportName: "Payroll Register",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "PayrollRegister-Project",
          reportName: "Payroll Register - Project",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "Banklist-SBC",
          reportName: "Bank List - SBC",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "Banklist-BDO",
          reportName: "Bank List - BDO",
          exportTypes: ["PDF", "RTF", "EXCEL"],
        },
        {
          value: "PayrollPayslip - PayslipSubQ",
          reportName: "Payroll Payslip",
          exportTypes: ["PDF", "RTF"],
        },
        {
          value: "PayslipCarbonized - modified",
          reportName: "Carbonized Payslip",
          exportTypes: ["PDF", "RTF"],
        },
      ],
      individualPayslipReports: [
        {
          value: "PayrollPayslip",
          reportName: "Payroll Payslip - Save and Download",
          exportTypes: ["PDF"],
          saveOnly: true,
        },
        {
          value: "PayrollPayslip - PayslipSubQ - Individual",
          reportName: "Payroll Payslip - Individual",
          exportTypes: ["PDF", "RTF"],
        },
        {
          value: "PayslipCarbonized - modified - Individual",
          reportName: "Carbonized Payslip - Individual",
          exportTypes: ["PDF", "RTF"],
        },
      ],
      backendReports: [
        { value: "Earnings", reportName: "Earnings" },
        { value: "Deductions", reportName: "Deductions" },
        { value: "Loans", reportName: "Loans" },
        { value: "LogHours", reportName: "Prooflist" },
        { value: "Overtime", reportName: "Overtime" },
        { value: "PayRegister", reportName: "Pay Register" },
      ],
      selected: [],
      selectedUserIds: [],
      employeeSearch: "",
      activeReportTab: "standard",
      passwordScope: "PAYSLIPS_ONLY",
      individualPasswordMode: "DEFAULT",
      sharedReportPassword: "",
      employeeTinNumbers: {},
      passwordToOpen: "",
      generationStartedAt: null,
      generationTimer: null,
      employeeStatusLookup: { status: "Active", value: 2 },
      employmentStatusLookup: { employStatus: "Regular", value: 3 },
      department: { departmentId: 0, departmentName: "All" },
    };
  },
  created() {
    this.$store.dispatch("lookup/getAllBankLookup");
    this.$store.dispatch("lookup/getAllDepartment");
  },
  computed: {
    getAllLogHours() {
      return this.$store.state.payroll.getAllLogHours;
    },
    tenantName() {
      return (id) => this.$store.getters["tenants/tenantName"](id);
    },
    getBankLookup() {
      return this.$store.state.lookup.getAllBankLookup;
    },
    getEmployeeStatusLookup() {
      return this.$store.state.lookup.employeeStatus;
    },
    getEmploymentStatusLookup() {
      return this.$store.state.lookup.employmentStatus;
    },
    getDepartmentLookup() {
      return [
        { departmentId: 0, departmentName: "All" },
        ...this.$store.state.lookup.getAllDepartment,
      ];
    },
    bankName() {
      return (id) => this.$store.getters["lookup/bankLookup"](id);
    },
    payrollEmployees() {
      const employeesById = new Map();

      this.getAllLogHours.forEach((logHour) => {
        const user = logHour.user || {};
        const userId = logHour.userId || user.id;
        if (!userId || employeesById.has(userId)) return;

        employeesById.set(userId, {
          userId,
          employeeCode: user.employeeCode || logHour.employeeCode || "",
          firstName: user.firstName || logHour.firstName || "",
          lastName: user.lastName || logHour.lastName || "",
          fullName:
            user.fullName ||
            logHour.fullName ||
            user.employeeCode ||
            "Unnamed employee",
          tinNumber:
            user.tinNumber ||
            user.TINNumber ||
            logHour.tinNumber ||
            logHour.TINNumber ||
            "",
        });
      });

      return Array.from(employeesById.values()).sort((a, b) =>
        a.fullName.localeCompare(b.fullName)
      );
    },
    filteredPayrollEmployees() {
      const search = this.employeeSearch.trim().toLowerCase();
      if (!search) return this.payrollEmployees;

      return this.payrollEmployees.filter(
        (employee) =>
          employee.fullName.toLowerCase().includes(search) ||
          employee.employeeCode.toLowerCase().includes(search)
      );
    },
    areAllEmployeesSelected() {
      return (
        this.payrollEmployees.length > 0 &&
        this.payrollEmployees.every((employee) =>
          this.selectedUserIds.includes(employee.userId)
        )
      );
    },
    hasSelectedIndividualPayslip() {
      return this.individualPayslipReports.some((report) =>
        report.exportTypes.some((type) =>
          this.selected.includes(this.getReportSelectionValue(report, type))
        )
      );
    },
    selectedStandardReportCount() {
      return [...this.crystalReports, ...this.backendReports].filter((report) => {
        const exportTypes = report.exportTypes || ["EXCEL"];
        return exportTypes.some((type) => this.isReportSelected(report, type));
      }).length;
    },
    selectedReportCount() {
      return new Set(this.selectedReportItems.map((item) => item.value)).size;
    },
    selectedReportItems() {
      const reportItems = [];

      [...this.crystalReports, ...this.individualPayslipReports].forEach(
        (report) => {
        report.exportTypes.forEach((type) => {
          if (this.selected.includes(this.getReportSelectionValue(report, type))) {
            reportItems.push({ ...report, type });
          }
        });
        }
      );

      this.backendReports.forEach((report) => {
        if (this.selected.includes(this.getReportSelectionValue(report, "EXCEL"))) {
          reportItems.push({ ...report, type: "EXCEL" });
        }
      });

      return reportItems;
    },
  },
  methods: {
    getReportSelectionValue(report, type) {
      return `${type}:${report.value}`;
    },
    isReportSelected(report, type) {
      return this.selected.includes(this.getReportSelectionValue(report, type));
    },
    getReportSelectionValues(report) {
      return report.exportTypes.map((type) =>
        this.getReportSelectionValue(report, type)
      );
    },
    isAnyReportFormatSelected(report) {
      return this.getReportSelectionValues(report).some((value) =>
        this.selected.includes(value)
      );
    },
    areAllReportFormatsSelected(report) {
      return this.areSelectionsSelected(this.getReportSelectionValues(report));
    },
    toggleCrystalReport(report) {
      this.toggleSelections(this.getReportSelectionValues(report));
    },
    toggleBackendReport(report) {
      const value = this.getReportSelectionValue(report, "EXCEL");
      this.toggleSelections([value]);
    },
    getCrystalSelectionValues(type) {
      return this.crystalReports.reduce((values, report) => {
        report.exportTypes.forEach((exportType) => {
          if (!type || type === exportType) {
            values.push(this.getReportSelectionValue(report, exportType));
          }
        });
        return values;
      }, []);
    },
    getIndividualSelectionValues(type) {
      return this.individualPayslipReports.reduce((values, report) => {
        report.exportTypes.forEach((exportType) => {
          if (!type || type === exportType) {
            values.push(this.getReportSelectionValue(report, exportType));
          }
        });
        return values;
      }, []);
    },
    getBackendSelectionValues() {
      return this.backendReports.map((report) =>
        this.getReportSelectionValue(report, "EXCEL")
      );
    },
    areSelectionsSelected(values) {
      return values.length > 0 && values.every((value) => this.selected.includes(value));
    },
    toggleSelections(values) {
      if (this.areSelectionsSelected(values)) {
        this.selected = this.selected.filter((value) => !values.includes(value));
        return;
      }

      this.selected = [...new Set([...this.selected, ...values])];
    },
    isCrystalFormatSelected(type) {
      return this.areSelectionsSelected(this.getCrystalSelectionValues(type));
    },
    areAllCrystalReportsSelected() {
      return this.areSelectionsSelected(this.getCrystalSelectionValues());
    },
    areAllBackendReportsSelected() {
      return this.areSelectionsSelected(this.getBackendSelectionValues());
    },
    isIndividualFormatSelected(type) {
      return this.areSelectionsSelected(
        this.getIndividualSelectionValues(type)
      );
    },
    areAllIndividualReportsSelected() {
      return this.areSelectionsSelected(this.getIndividualSelectionValues());
    },
    toggleCrystalFormat(type) {
      this.toggleSelections(this.getCrystalSelectionValues(type));
    },
    toggleAllCrystalReports() {
      this.toggleSelections(this.getCrystalSelectionValues());
    },
    toggleAllBackendReports() {
      this.toggleSelections(this.getBackendSelectionValues());
    },
    toggleIndividualFormat(type) {
      this.toggleSelections(this.getIndividualSelectionValues(type));
    },
    toggleAllIndividualReports() {
      this.toggleSelections(this.getIndividualSelectionValues());
    },
    toggleEmployee(userId) {
      if (this.selectedUserIds.includes(userId)) {
        this.selectedUserIds = this.selectedUserIds.filter(
          (selectedUserId) => selectedUserId !== userId
        );
        return;
      }

      this.selectedUserIds = [...this.selectedUserIds, userId];
    },
    toggleAllEmployees() {
      if (this.areAllEmployeesSelected) {
        this.selectedUserIds = [];
        return;
      }

      this.selectedUserIds = this.payrollEmployees.map(
        (employee) => employee.userId
      );
    },
    formatElapsedTime(milliseconds) {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    startGenerationTimer() {
      this.stopGenerationTimer();
      this.generationStartedAt = Date.now();
      this.generationTimer = window.setInterval(() => {
        const elapsedElement = document.getElementById(
          "report-generation-elapsed"
        );
        if (elapsedElement) {
          elapsedElement.textContent = `Elapsed time: ${this.formatElapsedTime(
            Date.now() - this.generationStartedAt
          )}`;
        }
      }, 1000);
    },
    stopGenerationTimer() {
      if (this.generationTimer) {
        window.clearInterval(this.generationTimer);
        this.generationTimer = null;
      }
    },
    updateGenerationStatus(status, progress) {
      const statusElement = document.getElementById("report-generation-status");
      const progressElement = document.getElementById(
        "report-generation-progress"
      );
      const progressBar = document.getElementById(
        "report-generation-progress-bar"
      );
      const progressText = document.getElementById(
        "report-generation-progress-text"
      );

      if (statusElement) statusElement.textContent = status;

      if (progressElement && progressBar && progressText) {
        if (typeof progress === "number") {
          const percentage = Math.min(100, Math.max(0, Math.round(progress)));
          progressElement.classList.remove("report-progress--hidden");
          progressBar.style.width = `${percentage}%`;
          progressText.textContent = `${percentage}%`;
        } else {
          progressElement.classList.add("report-progress--hidden");
        }
      }
    },
    displayGenerateReportsError(error) {
      this.stopGenerationTimer();
      const responseMessage =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message;
      const message =
        responseMessage ||
        (error && error.data && error.data.message) ||
        (error && error.message);
      let text =
        message && typeof message === "object" ? message.text : message;
      if (text === "Overtime is empty") {
        text = `Excel-${text}`;
      }
      const type =
        message && typeof message === "object" && message.type
          ? message.type.toLowerCase()
          : "error";
      const supportedIcons = ["success", "error", "warning", "info", "question"];

      this.$vs.loading.close();
      Swal.fire({
        title: "Generate Reports",
        text: text || "Unable to generate reports.",
        icon: supportedIcons.includes(type) ? type : "error",
      });
    },
    sanitizeFileName(value) {
      return String(value || "employee")
        .replace(/[\\/:*?"<>|]/g, "")
        .trim();
    },
    getEmployeeArchiveName(employee) {
      const fullName = String(employee.fullName || "Employee").trim();
      const commaSeparatedName = fullName.split(",").map((name) => name.trim());
      const spaceSeparatedName = fullName.split(/\s+/);
      const fallbackLastName =
        commaSeparatedName.length > 1
          ? commaSeparatedName[0]
          : spaceSeparatedName[spaceSeparatedName.length - 1];
      const fallbackFirstName =
        commaSeparatedName.length > 1
          ? commaSeparatedName.slice(1).join(" ")
          : spaceSeparatedName.slice(0, -1).join(" ");
      const lastName = employee.lastName || fallbackLastName || "Employee";
      const firstName = employee.firstName || fallbackFirstName || "";
      const employeeName = firstName
        ? `${lastName},${firstName}`
        : lastName;

      return this.sanitizeFileName(
        `${employeeName} - ${employee.employeeCode}`
      );
    },
    readBlobAsArrayBuffer(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () =>
          reject(reader.error || new Error("Unable to prepare the payslip ZIP."));
        reader.readAsArrayBuffer(blob);
      });
    },
    async validateDownloadedArchive(data) {
      const archive =
        data instanceof Blob
          ? data
          : new Blob([data], { type: "application/zip" });
      const signatureBuffer = await this.readBlobAsArrayBuffer(
        archive.slice(0, 4)
      );
      const signature = Array.from(new Uint8Array(signatureBuffer));
      const isZip =
        signature.length >= 4 &&
        signature[0] === 0x50 &&
        signature[1] === 0x4b &&
        ((signature[2] === 0x03 && signature[3] === 0x04) ||
          (signature[2] === 0x05 && signature[3] === 0x06) ||
          (signature[2] === 0x07 && signature[3] === 0x08));

      if (!isZip) {
        throw new Error(
          "The server did not return a valid ZIP file. Please generate the reports again."
        );
      }

      return archive;
    },
    downloadReportFile(data, fileName) {
      const blob =
        data instanceof Blob
          ? data
          : new Blob([data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
    normalizeDefaultPassword(employee) {
      return `${employee.lastName || ""}${employee.employeeCode || ""}`
        .replace(/[\s,.]/g, "")
        .toLowerCase();
    },
    normalizeTinPassword(tinNumber) {
      return String(tinNumber || "").replace(/\D/g, "");
    },
    async loadSelectedEmployeeTinNumbers() {
      const employees = this.payrollEmployees.filter((employee) =>
        this.selectedUserIds.includes(employee.userId)
      );
      const tinNumbers = {};

      for (let index = 0; index < employees.length; index += 1) {
        const employee = employees[index];
        let tinNumber = this.normalizeTinPassword(employee.tinNumber);

        if (!tinNumber) {
          const response = await this.$store.dispatch(
            "hrms/getAllSalaryInformation",
            employee.userId
          );
          const salaryInformation = response.data.data || {};
          tinNumber = this.normalizeTinPassword(
            salaryInformation.tinNumber || salaryInformation.TINNumber
          );
        }

        tinNumbers[employee.userId] = tinNumber;
      }

      return tinNumbers;
    },
    getMissingDefaultPasswordEmployees() {
      return this.payrollEmployees.filter(
        (employee) =>
          this.selectedUserIds.includes(employee.userId) &&
          (!employee.lastName || !employee.employeeCode)
      );
    },
    getEmployeeListSummary(employees) {
      const visibleNames = employees
        .slice(0, 3)
        .map((employee) => employee.fullName)
        .join(", ");
      const remainingCount = employees.length - 3;

      return remainingCount > 0
        ? `${visibleNames}, and ${remainingCount} more`
        : visibleNames;
    },
    getPasswordForJob(job) {
      if (this.passwordScope === "NONE") return null;
      if (this.passwordScope === "ALL_REPORTS") {
        return this.sharedReportPassword;
      }
      if (!job.isIndividual) return null;

      switch (this.individualPasswordMode) {
        case "DEFAULT":
          return this.normalizeDefaultPassword(job.employee);
        case "TIN":
          return this.employeeTinNumbers[job.userId] || "";
        case "CUSTOM":
          return this.sharedReportPassword;
        default:
          return null;
      }
    },
    clearSensitivePasswordData() {
      this.passwordToOpen = "";
      this.sharedReportPassword = "";
      this.employeeTinNumbers = {};
    },
    getPasswordConfigurationHtml() {
      const payslipScopeOption = this.hasSelectedIndividualPayslip
        ? `
          <label class="report-password-option">
            <input type="radio" name="report-password-scope" value="PAYSLIPS_ONLY" checked>
            <span>
              <strong>Individual payslips only</strong>
              <small>Standard reports will not receive a password.</small>
            </span>
          </label>
        `
        : "";
      const payslipPasswordOptions = this.hasSelectedIndividualPayslip
        ? `
          <div id="individual-password-options" class="report-password-group">
            <p class="report-password-group__title">Individual payslip password</p>
            <label class="report-password-option">
              <input type="radio" name="individual-password-mode" value="DEFAULT" checked>
              <span><strong>Employee default</strong><small>Lowercase last name + employee code.</small></span>
            </label>
            <label class="report-password-option">
              <input type="radio" name="individual-password-mode" value="TIN">
              <span><strong>TIN number</strong><small>Uses the employee's TIN digits.</small></span>
            </label>
            <label class="report-password-option">
              <input type="radio" name="individual-password-mode" value="CUSTOM">
              <span><strong>One password for all</strong><small>Use one password for every selected employee.</small></span>
            </label>
            <label class="report-password-option">
              <input type="radio" name="individual-password-mode" value="NONE">
              <span><strong>No password</strong><small>Generate individual payslips without password protection.</small></span>
            </label>
          </div>
        `
        : "";

      return `
        <div class="report-password-dialog">
          <p class="report-password-intro">Choose where password protection should apply.</p>
          <div class="report-password-group">
            ${payslipScopeOption}
            <label class="report-password-option">
              <input type="radio" name="report-password-scope" value="ALL_REPORTS" ${
                this.hasSelectedIndividualPayslip ? "" : "checked"
              }>
              <span><strong>All selected reports</strong><small>Use one password for every generated report.</small></span>
            </label>
            <label class="report-password-option">
              <input type="radio" name="report-password-scope" value="NONE">
              <span><strong>No password protection</strong><small>Generate all selected reports without a password.</small></span>
            </label>
          </div>
          ${payslipPasswordOptions}
          <div id="shared-report-password-wrapper" class="report-password-input report-password-input--hidden">
            <label for="shared-report-password">Password</label>
            <input id="shared-report-password" type="password" maxlength="10" autocomplete="new-password" placeholder="Maximum of 10 characters">
            <button id="toggle-shared-report-password" type="button">Show</button>
          </div>
          <p class="report-password-security-note">Passwords are used only for this generation request.</p>
        </div>
      `;
    },
    updatePasswordConfigurationDialog() {
      const container = Swal.getHtmlContainer();
      if (!container) return;
      const selectedScope = container.querySelector(
        'input[name="report-password-scope"]:checked'
      );
      const selectedMode = container.querySelector(
        'input[name="individual-password-mode"]:checked'
      );
      const payslipOptions = container.querySelector(
        "#individual-password-options"
      );
      const passwordWrapper = container.querySelector(
        "#shared-report-password-wrapper"
      );
      const scope = selectedScope ? selectedScope.value : "NONE";
      const mode = selectedMode ? selectedMode.value : "DEFAULT";
      const showPayslipOptions =
        Boolean(payslipOptions) && scope === "PAYSLIPS_ONLY";
      const showPasswordInput =
        scope === "ALL_REPORTS" ||
        (scope === "PAYSLIPS_ONLY" && mode === "CUSTOM");

      if (payslipOptions) {
        payslipOptions.classList.toggle(
          "report-password-group--disabled",
          !showPayslipOptions
        );
        payslipOptions
          .querySelectorAll('input[type="radio"]')
          .forEach((input) => {
            input.disabled = !showPayslipOptions;
          });
      }
      if (passwordWrapper) {
        passwordWrapper.classList.toggle(
          "report-password-input--hidden",
          !showPasswordInput
        );
      }
    },
    async validatePasswordConfiguration() {
      const container = Swal.getHtmlContainer();
      const scopeInput = container.querySelector(
        'input[name="report-password-scope"]:checked'
      );
      const modeInput = container.querySelector(
        'input[name="individual-password-mode"]:checked'
      );
      const passwordInput = container.querySelector(
        "#shared-report-password"
      );
      const scope = scopeInput ? scopeInput.value : "NONE";
      const mode = modeInput ? modeInput.value : "DEFAULT";
      const sharedPassword = passwordInput ? passwordInput.value : "";
      const requiresSharedPassword =
        scope === "ALL_REPORTS" ||
        (scope === "PAYSLIPS_ONLY" && mode === "CUSTOM");

      if (requiresSharedPassword && !sharedPassword) {
        Swal.showValidationMessage("Enter the password to use for the selected reports.");
        return false;
      }

      let tinNumbers = {};
      if (scope === "PAYSLIPS_ONLY" && mode === "DEFAULT") {
        const missingEmployees = this.getMissingDefaultPasswordEmployees();
        if (missingEmployees.length) {
          Swal.showValidationMessage(
            "Missing last name or employee code: " +
              this.getEmployeeListSummary(missingEmployees)
          );
          return false;
        }
      }

      if (scope === "PAYSLIPS_ONLY" && mode === "TIN") {
        try {
          tinNumbers = await this.loadSelectedEmployeeTinNumbers();
        } catch (error) {
          Swal.showValidationMessage(
            "Unable to retrieve employee TIN information. Please try again."
          );
          return false;
        }
        const missingEmployees = this.payrollEmployees.filter(
          (employee) =>
            this.selectedUserIds.includes(employee.userId) &&
            !tinNumbers[employee.userId]
        );
        if (missingEmployees.length) {
          Swal.showValidationMessage(
            "Missing TIN number: " +
              this.getEmployeeListSummary(missingEmployees)
          );
          return false;
        }
      }

      return {
        scope,
        mode:
          scope === "ALL_REPORTS"
            ? "CUSTOM"
            : scope === "NONE"
            ? "NONE"
            : mode,
        sharedPassword,
        tinNumbers,
      };
    },
    async onGenerateReports() {
      this.percentageReport = [0];
      this.counterReports = 0;
      const tenant =
        this.tenantName(this.$store.state.AppActiveUser.tenantsId) ||
        this.$store.state.tenants.tenants[0];

      try {
        this.updateGenerationStatus(
          "Preparing report data. Large selections may take several minutes."
        );

        const baseData = {
          serverName: tenant.secretKey,
          dbName: tenant.apiKey,
          userName: "",
          password: "",
          companyCode: tenant.code,
          companyName: tenant.name,
          address: tenant.address,
          photoURL: "",
          createPayrollId: this.createPayroll.createPayrollId,
          printedBy: this.$store.state.AppActiveUser.uid,
          payOutDate: moment(this.createPayroll.payOutDate).format("YYYYMMDD"),
          creditDate: moment(this.createPayroll.payOutDate).format("DD MMM yyyy"),
          passwordToOpen: null,
          monthName: "",
          yearScope: "",
          reportType: "CURRENT",
          employeeStatusLookup: this.employeeStatusLookup.value,
          employmentStatusLookup: this.employmentStatusLookup.value,
          department: this.department.departmentId,
        };
        const pDate = moment(this.createPayroll.payOutDate).format("YYYYMMDD");
        const zipFileName = tenant.code + "_" + pDate + ".zip";
        const individualReportValues = new Set(
          this.individualPayslipReports.map((report) => report.value)
        );
        const standardReportItems = this.selectedReportItems.filter(
          (item) => !individualReportValues.has(item.value)
        );
        const standardPayslipValues = new Set([
          "PayrollPayslip - PayslipSubQ",
          "PayslipCarbonized - modified",
        ]);
        const standardPayslipReportItems = standardReportItems.filter((item) =>
          standardPayslipValues.has(item.value)
        );
        const otherStandardReportItems = standardReportItems.filter(
          (item) => !standardPayslipValues.has(item.value)
        );
        const individualReportItems = this.selectedReportItems.filter((item) =>
          individualReportValues.has(item.value) && !item.saveOnly
        );
        const savedPayslipReportItems = this.selectedReportItems.filter(
          (item) => individualReportValues.has(item.value) && item.saveOnly
        );
        const jobs = [];
        const standardArchives = [];
        const individualPayslipZip = new JSZip();
        let individualArchiveCount = 0;
        let standardArchiveCount = 0;
        let savedPayslipCount = 0;

        if (otherStandardReportItems.length) {
          jobs.push({
            label: "standard reports",
            reportItems: otherStandardReportItems,
            userId: this.$store.state.AppActiveUser.uid,
            isIndividual: false,
          });
        }

        if (standardPayslipReportItems.length) {
          jobs.push({
            label: "payslip reports",
            reportItems: standardPayslipReportItems,
            userId: this.$store.state.AppActiveUser.uid,
            isIndividual: false,
            isStandardPayslip: true,
          });
        }

        if (individualReportItems.length) {
          this.payrollEmployees
            .filter((employee) =>
              this.selectedUserIds.includes(employee.userId)
            )
            .forEach((employee) => {
              jobs.push({
                label: "payslip for " + employee.fullName,
                reportItems: individualReportItems,
                userId: employee.userId,
                employee,
                isIndividual: true,
              });
            });
        }

        if (savedPayslipReportItems.length) {
          this.payrollEmployees
            .filter((employee) =>
              this.selectedUserIds.includes(employee.userId)
            )
            .forEach((employee) => {
              jobs.push({
                label: "saved payslip for " + employee.fullName,
                reportItems: savedPayslipReportItems,
                userId: employee.userId,
                employee,
                isIndividual: true,
                saveOnly: true,
              });
            });
        }

        for (let jobIndex = 0; jobIndex < jobs.length; jobIndex += 1) {
          const job = jobs[jobIndex];
          const jobNumber = jobIndex + 1;
          const statusPrefix = "File " + jobNumber + " of " + jobs.length;
          this.updateGenerationStatus(
            statusPrefix + ": Generating " + job.label + "...",
            (jobIndex / jobs.length) * 100
          );
          const selectedExportTypes = [
            ...new Set(job.reportItems.map((item) => item.type)),
          ];
          const jobExportTypes = job.isStandardPayslip
            ? selectedExportTypes.filter(
                (type) => type === "PDF" || type === "RTF"
              )
            : selectedExportTypes;

          const generationResponse = await this.$store.dispatch(
            "payroll/GenerateReports",
            {
              ...baseData,
              passwordToOpen: this.getPasswordForJob(job),
              passwordScope: this.passwordScope,
              individualPasswordMode: job.isIndividual
                ? this.individualPasswordMode
                : null,
              reportName: [
                ...new Set(job.reportItems.map((item) => item.value)),
              ],
              ExportTypes: jobExportTypes,
              userId: [job.userId],
            }
          );

          if (
            generationResponse.data &&
            generationResponse.data.message &&
            generationResponse.data.message.text
          ) {
            throw generationResponse;
          }

          if (job.saveOnly) {
            this.updateGenerationStatus(
              statusPrefix + ": Downloading saved payslip...",
              (jobIndex / jobs.length) * 100
            );
            const payslipResponse = await axios.get("/api/reports/payslip", {
              params: {
                userId: job.userId,
                payOutDate: pDate,
                payslipType: "standard",
              },
              reportProgress: true,
              responseType: "blob",
            });
            const payslipFile = await this.readBlobAsArrayBuffer(
              payslipResponse.data
            );
            individualPayslipZip.file(
              this.getEmployeeArchiveName(job.employee) + ".pdf",
              payslipFile,
              { binary: true, compression: "STORE" }
            );
            individualArchiveCount += 1;
            savedPayslipCount += 1;
            this.updateGenerationStatus(
              statusPrefix + ": Payslip saved and added to the download.",
              (jobNumber / jobs.length) * 100
            );
            continue;
          }

          this.updateGenerationStatus(
            statusPrefix + ": Downloading " + job.label + "...",
            (jobIndex / jobs.length) * 100
          );

          const downloadResponse = await axios.get(
            "/api/reports/DownloadFile?dbName=" +
              tenant.code +
              "&fileName=" +
              zipFileName +
              "&payOutDate=" +
              pDate +
              "&reportType=CURRENT",
            {
              reportProgress: true,
              responseType: "blob",
              onDownloadProgress: (progressEvent) => {
                const loaded =
                  progressEvent.loaded ||
                  (progressEvent.event && progressEvent.event.loaded);
                const total =
                  progressEvent.total ||
                  (progressEvent.event && progressEvent.event.total);
                if (loaded && total) {
                  this.updateGenerationStatus(
                    statusPrefix + ": Downloading " + job.label + "...",
                    ((jobIndex + loaded / total) / jobs.length) * 100
                  );
                }
              },
            }
          );

          const downloadedArchive = await this.validateDownloadedArchive(
            downloadResponse.data
          );

          if (job.isIndividual) {
            const employeeArchive = await this.readBlobAsArrayBuffer(
              downloadedArchive
            );
            individualPayslipZip.file(
              this.getEmployeeArchiveName(job.employee) + ".zip",
              employeeArchive,
              { binary: true, compression: "STORE" }
            );
            individualArchiveCount += 1;
          } else {
            standardArchives.push({
              data: downloadedArchive,
              fileName: job.isStandardPayslip
                ? tenant.code + "_Payslip_Reports_" + pDate + ".zip"
                : zipFileName,
            });
          }
          this.updateGenerationStatus(
            statusPrefix + ": Download complete.",
            (jobNumber / jobs.length) * 100
          );
        }

        if (standardArchives.length) {
          standardArchives.forEach((archive) => {
            this.downloadReportFile(archive.data, archive.fileName);
          });
          standardArchiveCount = standardArchives.length;
        }

        if (individualArchiveCount) {
          this.updateGenerationStatus(
            "Packaging " +
              individualArchiveCount +
              " employee payslip" +
              (individualArchiveCount === 1 ? "" : "s") +
              " into one ZIP...",
            100
          );
          const combinedPayslipArchive = individualPayslipZip.generate({
            type: "blob",
            compression: "STORE",
          });
          this.downloadReportFile(
            combinedPayslipArchive,
            tenant.code + "_Individual_Payslips_" + pDate + ".zip"
          );
        }

        this.stopGenerationTimer();
        const downloadedArchiveCount =
          standardArchiveCount + (individualArchiveCount ? 1 : 0);
        this.clearSensitivePasswordData();
        Swal.fire({
          title: "Reports ready",
          text: savedPayslipCount
            ? downloadedArchiveCount
              ? `${downloadedArchiveCount} report ZIP file${
                  downloadedArchiveCount === 1 ? " was" : "s were"
                } downloaded, and ${savedPayslipCount} employee payslip${
                  savedPayslipCount === 1 ? " was" : "s were"
                } saved.`
              : `${savedPayslipCount} employee payslip${
                  savedPayslipCount === 1 ? " was" : "s were"
                } generated and saved.`
            : downloadedArchiveCount === 1
            ? "Your ZIP file has been downloaded."
            : `Your ${downloadedArchiveCount} report ZIP files have been downloaded.`,
          icon: "success",
          confirmButtonText: "Done",
        });
      } catch (error) {
        this.clearSensitivePasswordData();
        this.displayGenerateReportsError(error);
      }
    },
    onSetPasswordToOpen() {
      if (
        this.hasSelectedIndividualPayslip &&
        this.selectedUserIds.length === 0
      ) {
        this.activeReportTab = "individual";
        Swal.fire({
          title: "Select payslip recipients",
          text: "Choose at least one employee before generating an individual payslip.",
          icon: "warning",
          confirmButtonText: "Select employees",
        });
        return;
      }

      Swal.fire({
        title: "Report protection",
        html: this.getPasswordConfigurationHtml(),
        showCancelButton: true,
        confirmButtonText: "Continue",
        focusConfirm: false,
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        didOpen: () => {
          const container = Swal.getHtmlContainer();
          container
            .querySelectorAll(
              'input[name="report-password-scope"], input[name="individual-password-mode"]'
            )
            .forEach((input) => {
              input.addEventListener("change", () =>
                this.updatePasswordConfigurationDialog()
              );
            });
          const passwordInput = container.querySelector(
            "#shared-report-password"
          );
          const togglePassword = container.querySelector(
            "#toggle-shared-report-password"
          );
          if (passwordInput && togglePassword) {
            passwordInput.addEventListener("keydown", (event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                Swal.clickConfirm();
              }
            });
            togglePassword.addEventListener("click", () => {
              const showPassword = passwordInput.type === "password";
              passwordInput.type = showPassword ? "text" : "password";
              togglePassword.textContent = showPassword ? "Hide" : "Show";
            });
          }
          this.updatePasswordConfigurationDialog();
        },
        preConfirm: () => this.validatePasswordConfiguration(),
      }).then((t) => {
        if (!t.isConfirmed) return;
        this.passwordScope = t.value.scope;
        this.individualPasswordMode = t.value.mode;
        this.sharedReportPassword = t.value.sharedPassword;
        this.employeeTinNumbers = t.value.tinNumbers;
        this.onOpenWaitingDialog();
      });
    },
    onOpenWaitingDialog() {
      Swal.fire({
        title: "Generating reports",
        html: `
          <div class="report-generation-dialog">
            <p id="report-generation-status">Starting report generation...</p>
            <p id="report-generation-elapsed" class="report-generation-elapsed">Elapsed time: 0:00</p>
            <div id="report-generation-progress" class="report-progress report-progress--hidden">
              <div class="report-progress__track">
                <div id="report-generation-progress-bar" class="report-progress__bar"></div>
              </div>
              <span id="report-generation-progress-text" class="report-progress__text">0%</span>
            </div>
            <p class="report-generation-note">You can keep this window open while the server finishes the selected reports.</p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
          this.startGenerationTimer();
          this.onGenerateReports();
        },
      });
    },
  },
  beforeDestroy() {
    this.stopGenerationTimer();
  },
  components: {
    "v-select": vSelect,
  },
};
</script>
<style lang="scss">
@import "@/assets/scss/vuexy/apps/email.scss";
@import "@/assets/scss/vuexy/apps/simple-calendar.scss";

.report-tabs {
  display: inline-flex;
  gap: 0.3rem;
  padding: 0.3rem;
  border-radius: 9px;
  background: #f0f1f5;
}

.report-tab {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border: 0;
  border-radius: 7px;
  color: #6b7280;
  background: transparent;
  box-shadow: none;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    color: #5b4bc4;
  }

  &--active {
    color: #5b4bc4;
    background: #fff;
    box-shadow: 0 2px 8px rgba(34, 41, 47, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #7367f0;
    outline-offset: 2px;
  }
}

.report-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 0.35rem;
  border-radius: 999px;
  color: #fff;
  background: #7367f0;
  font-size: 0.68rem;
}

.report-section {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;
}

.report-section--backend {
  background: #fbfcfe;
}

.report-section__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e4e7ec;
}

.report-section__controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.report-selection-count {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
}

.report-select-button {
  padding: 0.4rem 0.75rem;
  border: 1px solid rgba(115, 103, 240, 0.35);
  border-radius: 6px;
  color: #5b4bc4;
  background: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover,
  &--active {
    border-color: #7367f0;
    color: #fff;
    background: #7367f0;
  }
}

.report-select-button--backend {
  border-color: rgba(40, 199, 111, 0.45);
  color: #087f5b;

  &:hover,
  &.report-select-button--active {
    border-color: #28c76f;
    background: #28c76f;
  }
}

.report-source-badge {
  flex-shrink: 0;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

.report-source-badge--crystal {
  color: #5b4bc4;
  background: rgba(115, 103, 240, 0.12);
}

.report-source-badge--backend {
  color: #087f5b;
  background: rgba(40, 199, 111, 0.13);
}

.report-table-wrapper {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;

  th,
  td {
    padding: 0.9rem 1.25rem;
    border-bottom: 1px solid #edf0f4;
    text-align: center;
  }

  th {
    color: #6b7280;
    background: #f8f9fb;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .report-format-toggle {
    --format-color: #7367f0;
    --format-soft-color: rgba(115, 103, 240, 0.1);
    appearance: none;
    width: 100%;
    padding: 0;
    border: 0 !important;
    color: inherit;
    background: transparent;
    box-shadow: none !important;
    cursor: pointer;
    font: inherit;
    text-align: center;

    &--pdf {
      --format-color: #d64545;
      --format-soft-color: rgba(214, 69, 69, 0.1);
    }

    &--rtf {
      --format-color: #7367f0;
      --format-soft-color: rgba(115, 103, 240, 0.1);
    }

    &--excel {
      --format-color: #16845b;
      --format-soft-color: rgba(22, 132, 91, 0.1);
    }

    &:hover .report-format-toggle__selector,
    &--active .report-format-toggle__selector {
      color: var(--format-color);
    }

    &:focus-visible {
      outline: 2px solid var(--format-color);
      outline-offset: 4px;
      border-radius: 4px;
    }
  }

  .report-format-toggle__label {
    display: inline-block;
    padding: 0.25rem 0.65rem;
    border-radius: 999px;
    color: var(--format-color);
    background: var(--format-soft-color);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.045em;
  }

  .report-format-toggle__selector {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin-top: 0.45rem;
    color: #98a0aa;
    font-size: 0.65rem;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
    transition: color 0.2s ease;
  }

  .report-format-toggle__indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border: 0;
    border-radius: 50%;
    background: #e9ebef;
    font-size: 0.65rem;
    line-height: 1;
  }

  .report-format-toggle--active .report-format-toggle__indicator {
    color: #fff;
    background: var(--format-color);
  }

  th:first-child,
  td:first-child {
    width: 58%;
    text-align: left;
  }

  th:not(:first-child),
  td:not(:first-child) {
    width: 14%;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover {
    background: #fafbff;
  }

  .report-name-cell {
    cursor: pointer;
    transition: color 0.2s ease, background 0.2s ease;

    &:hover,
    &--active {
      color: #5b4bc4;
      background: rgba(115, 103, 240, 0.045);
    }

    &:focus-visible {
      outline: 2px solid #7367f0;
      outline-offset: -3px;
    }
  }

  .report-name-cell__hint {
    display: block;
    margin-top: 0.18rem;
    color: #98a0aa;
    font-size: 0.68rem;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
  }

  .con-vs-checkbox {
    justify-content: center;
    margin: 0 auto;
  }
}

.report-checkbox-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 24px;
}

.report-format-unavailable {
  color: #c4c8cf;
}

.backend-report-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
  padding: 1.25rem 1.5rem 1.5rem;
}

.backend-report-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 68px;
  padding: 0.9rem 1rem;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover,
  &--active {
    border-color: #7367f0;
    box-shadow: 0 3px 10px rgba(34, 41, 47, 0.07);
  }

  &--active {
    background: rgba(115, 103, 240, 0.06);
  }

  &:focus-visible {
    outline: 2px solid #7367f0;
    outline-offset: 2px;
  }
}

.employee-selection-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #edf0f4;
}

.employee-search {
  width: 100%;
  max-width: 380px;
}

.employee-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  max-height: 380px;
  padding: 1rem 1.5rem 1.5rem;
  overflow-y: auto;
}

.employee-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  padding: 0.8rem 0.9rem;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease,
    box-shadow 0.2s ease;

  &:hover,
  &--active {
    border-color: #7367f0;
    box-shadow: 0 3px 10px rgba(34, 41, 47, 0.07);
  }

  &--active {
    background: rgba(115, 103, 240, 0.06);
  }

  &:focus-visible {
    outline: 2px solid #7367f0;
    outline-offset: 2px;
  }
}

.employee-option__details {
  display: flex;
  min-width: 0;
  flex-direction: column;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.employee-empty-state {
  padding: 2.5rem 1.5rem;
  color: #8a8f98;
  text-align: center;
}

.report-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.report-password-dialog {
  color: #4b5563;
  text-align: left;
}

.report-password-intro {
  margin: 0 0 0.85rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.report-password-group {
  display: grid;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 9px;
  background: #f7f8fb;
  transition: opacity 0.2s ease;
}

.report-password-group--disabled {
  display: none;
}

.report-password-group__title {
  margin: 0 0 0.15rem;
  color: #2f3542;
  font-size: 0.8rem;
  font-weight: 700;
}

.report-password-option {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.65rem 0.7rem;
  border: 1px solid #e4e7ec;
  border-radius: 7px;
  background: #fff;
  cursor: pointer;

  input {
    flex-shrink: 0;
    margin-top: 0.2rem;
    accent-color: #7367f0;
  }

  span {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  strong {
    color: #2f3542;
    font-size: 0.82rem;
  }

  small {
    color: #7c8490;
    font-size: 0.72rem;
    line-height: 1.35;
  }
}

.report-password-input {
  position: relative;
  margin-top: 0.5rem;

  label {
    display: block;
    margin-bottom: 0.35rem;
    color: #2f3542;
    font-size: 0.78rem;
    font-weight: 600;
  }

  input {
    width: 100%;
    height: 40px;
    padding: 0 4.2rem 0 0.75rem;
    border: 1px solid #d9dde4;
    border-radius: 7px;
    outline: none;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.12);
    }
  }

  button {
    position: absolute;
    right: 0.45rem;
    bottom: 0.35rem;
    height: 30px;
    padding: 0 0.55rem;
    border: 0;
    color: #5b4bc4;
    background: transparent;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
  }
}

.report-password-input--hidden {
  display: none;
}

.report-password-security-note {
  margin: 0.8rem 0 0;
  color: #8a8f98;
  font-size: 0.7rem;
  text-align: center;
}

.report-generation-dialog {
  padding-top: 0.25rem;
}

.report-generation-elapsed {
  margin-top: 0.6rem;
  color: #6b7280;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}

.report-generation-note {
  margin-top: 1rem;
  color: #8a8f98;
  font-size: 0.78rem;
  line-height: 1.45;
}

.report-progress {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.report-progress--hidden {
  display: none;
}

.report-progress__track {
  flex: 1;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #ebe9fd;
}

.report-progress__bar {
  width: 0;
  height: 100%;
  border-radius: inherit;
  background: #7367f0;
  transition: width 0.2s ease;
}

.report-progress__text {
  min-width: 3rem;
  color: #5b4bc4;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: right;
}

@media (max-width: 900px) {
  .backend-report-grid,
  .employee-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .report-section__header,
  .report-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .report-source-badge {
    align-self: flex-start;
  }

  .report-section__controls {
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;
  }

  .backend-report-grid {
    grid-template-columns: 1fr;
  }

  .report-tabs {
    display: flex;
  }

  .report-tab {
    flex: 1;
    justify-content: center;
  }

  .employee-selection-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .employee-search {
    max-width: none;
  }

  .employee-list {
    grid-template-columns: 1fr;
  }
}
</style>
